import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Alert, Button, Skeleton, Text } from "@mantine/core"
import { Link } from "@tanstack/react-router"
import { formatDateTime } from "../../../lib/format-date"
import { FinalizationProgress } from "../../FinalizationProgress"
import { resolveFinalizeMode } from "../../FinalizationProgress/progress"
import { Player } from "../../Player"
import { StaggerReveal } from "../../StaggerReveal"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

const SKELETON_HEIGHTS = [150, 190, 150, 120, 96, 120]

/** Widths of the placeholder transcript lines on the Lite skeleton. */
const LITE_LINE_WIDTHS = ["92%", "74%", "86%", "40%", "88%", "63%", "80%"]

/**
 * The post-recording "finalizing" surface: the recording player pops in early,
 * above a dimmed skeleton of the page that is about to appear. Full shows the
 * analysis cards. Lite shows a transcript column and a narrow side column.
 * `FinalizationProgress` floats in a dialog over it.
 *
 * Dumb: all state (stage, mode) is supplied by the host. `mode` wins; otherwise
 * `templateId` of `meeting-lite` selects the Lite steps and skeleton.
 */
export function MeetingFinalizingView({
  subject,
  startTime,
  speakerNames,
  recordingUrl,
  segments,
  speakerColors,
  chapters,
  stage,
  currentStepRef,
  mode: modeProp,
  templateId,
  delayed = false,
  meetingId,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const speakers = speakerNames ?? []
  const mode = resolveFinalizeMode({ mode: modeProp, templateId })
  const segmentCount = segments?.length ?? 0
  return (
    <div className={classes.stage}>
      <div className={classes.page}>
        <header className={classes.head}>
          <h1 className={classes.title}>{subject}</h1>
          {startTime && (
            <div className={classes.meta}>
              {formatDateTime(i18n, startTime)}
              {speakers.length > 0 ? (
                <>
                  {" · "}
                  <Plural value={speakers.length} one="# speaker" other="# speakers" />
                </>
              ) : null}
            </div>
          )}
        </header>

        {recordingUrl && (
          <div className={classes.playerSlot}>
            <StaggerReveal active>
              <Player
                src={recordingUrl}
                segments={segments}
                speakerColors={speakerColors}
                speakerNames={speakers}
                chapters={chapters}
              />
            </StaggerReveal>
          </div>
        )}

        {mode === "lite" ? (
          <div className={classes.liteBody} aria-hidden="true" data-testid="finalize-skeleton" data-mode="lite">
            <div className={classes.liteTranscript}>
              {LITE_LINE_WIDTHS.map((width, i) => (
                <Skeleton key={`line-${width}-${i}`} height={14} width={width} radius="sm" className={classes.skel} />
              ))}
            </div>
            <div className={classes.liteSide}>
              <Skeleton height={148} radius="md" className={classes.skel} />
              <Skeleton height={72} radius="md" className={classes.skel} />
            </div>
          </div>
        ) : (
          <div className={classes.grid} aria-hidden="true" data-testid="finalize-skeleton" data-mode="full">
            {SKELETON_HEIGHTS.map((h, i) => (
              <Skeleton key={`sk-${h}-${i}`} height={h} radius="md" className={classes.skel} />
            ))}
          </div>
        )}
      </div>

      <div className={classes.backdrop}>
        <div className={classes.dialog}>
          <FinalizationProgress
            stage={stage}
            mode={mode}
            currentStepRef={currentStepRef}
            voiceCount={speakers.length}
            segmentCount={segmentCount}
          />
          {delayed && (
            <Alert color="info" variant="light" mt="sm" data-testid="finalize-delayed">
              <Text size="sm" fw={600} mb={4}>
                <Trans>This is taking longer than usual</Trans>
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                <Trans>
                  Your recording is safe and still being processed on our side. You can keep waiting here, or come back
                  to it from the meeting — we&apos;ll have it ready when it&apos;s done.
                </Trans>
              </Text>
              {meetingId && (
                <Button
                  size="xs"
                  variant="light"
                  color="info"
                  renderRoot={(props) => <Link to="/meetings/$id" params={{ id: meetingId }} {...props} />}
                >
                  <Trans>View meeting</Trans>
                </Button>
              )}
            </Alert>
          )}
        </div>
      </div>
    </div>
  )
}
